// src/components/Groups/GroupsPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, CircularProgress, TextField, Button, Chip, Stack, IconButton, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { formatLocalDateOnly } from "../shared utils/dateFormatters";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RefreshIcon from "@mui/icons-material/Refresh";
import { SERVER_URL, IMPORTED_CHECK_STATUS } from "../../constants";
import AddBookingGroup from "./AddBookingGroup";
import EditIcon from "@mui/icons-material/Edit";
import EditBookingGroup from "./EditBookingGroup";
import { fetchStudentsByIds, fetchJsonAuth } from "./utils/studentsApi.js";
import { getGroupInvoice } from "./api/finance";
import { generateCombinedInvoicePDF } from "./utils/invoicePdf";
import { fetchJson } from "./utils/http";
import { statusLabel } from "./utils/bookingStatus";
import GroupsTimeline from "./GroupsTimeline";

const GROUPS_VIEW_KEY = "greaterbase_groups_view";



export default function GroupsPage({ showFinancials = false, selectedYear, onSelectStudent }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  // List or timeline, remembered per browser.
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(GROUPS_VIEW_KEY) === "timeline" ? "timeline" : "list"; } catch { return "list"; }
  });
  const changeView = (_e, next) => {
    if (!next) return;
    setView(next);
    try { localStorage.setItem(GROUPS_VIEW_KEY, next); } catch { /* private window */ }
  };
  const [addOpen, setAddOpen] = useState(false);
  const [reload, setReload] = useState(0); // 🔧 trigger refetch after create
  const [editOpen, setEditOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // expansion + members cache
  const [expanded, setExpanded] = useState({});
  const [membersByGroup, setMembersByGroup] = useState({}); // { [groupId]: { students: [...], adults: [...] } }
  const [loadingMembers, setLoadingMembers] = useState({}); // { [groupId]: boolean }

  // Multi-select for combined invoice
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  const toggleGroupSelection = (groupId) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedGroupIds((prev) =>
      prev.size === filtered.length
        ? new Set()
        : new Set(filtered.map((g) => g.id))
    );
  };

  const DISCOUNT_PREFIX = "DISCOUNT:";
  const isDiscountPayment = (p) =>
    String(p?.paymentAccount || "").toUpperCase().startsWith(DISCOUNT_PREFIX);

  // Build invoice lines from members (same logic as GroupFinanceTab)
  function buildInvoiceLines(members) {
    const lines = new Map();
    (members || []).forEach((member) => {
      const bookings = Array.isArray(member?.bookings) ? member.bookings : [];
      bookings.forEach((b) => {
        const charge = Number(b.actualCharge) || 0;
        const key = [String(b.productName || ""), String(b.startDate || ""), String(b.endDate || ""), charge.toFixed(2)].join("||");
        if (!lines.has(key)) {
          lines.set(key, { productName: b.productName || "", startDate: b.startDate || "", endDate: b.endDate || "", rate: charge, qty: 0, grossTotal: 0, netTotal: 0, bookingIds: [] });
        }
        const line = lines.get(key);
        line.qty += 1;
        line.grossTotal += charge;
        line.netTotal += charge;
        const bid = b.bookingId || b.id;
        if (bid) line.bookingIds.push(bid);
      });
    });
    return Array.from(lines.values()).sort((a, b) => {
      const da = new Date(a.startDate || 0).getTime();
      const db = new Date(b.startDate || 0).getTime();
      return da !== db ? da - db : String(a.productName).localeCompare(String(b.productName));
    });
  }

  // Combined invoice preview state
  const [invoicePreview, setInvoicePreview] = useState(null);
  // { groupNames, memberCount, invoiceLines, payments, discountPayments, discountTotal, paidExcludingDiscounts, invoiceTotal, balance }
  const [pdfExcluded, setPdfExcluded] = useState(new Set());
  const [invoiceHeaderNote, setInvoiceHeaderNote] = useState("");

  const invoiceLineKey = (line, idx) => `${line.productName}||${line.startDate}||${line.rate}||${idx}`;

  const togglePdfExclude = (key) => {
    setPdfExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCombinedInvoice = async () => {
    if (!selectedGroupIds.size) return;
    const token = sessionStorage.getItem("bearer");
    setGeneratingInvoice(true);
    try {
      let allLines = [];
      let allPayments = [];
      let totalMembers = 0;
      const names = [];

      // A member who sits in more than one of the selected groups (common with
      // placeholder/parallel groups) would otherwise have their bookings counted
      // once per group. Dedupe every booking by id across the whole combined run.
      const seenBookingIds = new Set();
      const dedupeBookings = (bookings) =>
        (Array.isArray(bookings) ? bookings : []).filter((b) => {
          const bid = b?.bookingId ?? b?.id;
          if (bid == null) return true;
          if (seenBookingIds.has(bid)) return false;
          seenBookingIds.add(bid);
          return true;
        });

      for (const gid of selectedGroupIds) {
        const group = groups.find((g) => g.id === gid);
        if (!group) continue;
        names.push(group.groupName || `Group ${gid}`);

        const invoice = await getGroupInvoice(gid, token);
        const members = (Array.isArray(invoice?.members) ? invoice.members : []).map(
          (m) => ({ ...m, bookings: dedupeBookings(m.bookings) })
        );
        const payments = Array.isArray(invoice?.groupPayments) ? invoice.groupPayments : [];
        allLines.push(...buildInvoiceLines(members));
        allPayments.push(...payments);
        totalMembers += (group.studentIds?.length || 0) + (group.adultIds?.length || 0);

        // Adult bookings — collect as fake member bookings for roll-up
        const adultBookingsList = [];
        for (const adultId of (group.adultIds || [])) {
          try {
            const res = await fetch(`${SERVER_URL}api/adults/${adultId}/adult-bookings`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) continue;
            const data = await res.json();
            for (const b of (Array.isArray(data) ? data : [])) {
              const charge = Number(b.actualCharge || 0);
              if (charge > 0 || b.participationType === "BOOKING") {
                adultBookingsList.push({
                  productName: `${b.productName || "Adult product"} (adult)`,
                  startDate: b.customStart || "", endDate: b.customEnd || "",
                  actualCharge: charge,
                  bookingId: b.id, id: b.id,
                });
              }
            }
          } catch (e) { /* skip */ }
        }
        const adultDeduped = dedupeBookings(adultBookingsList);
        if (adultDeduped.length) {
          // Roll up via buildInvoiceLines by wrapping as a single "member"
          const adultLines = buildInvoiceLines([{ bookings: adultDeduped }]);
          adultLines.forEach((l) => { l.isAdultBooking = true; });
          allLines.push(...adultLines);
        }
      }

      allLines.sort((a, b) => {
        const da = new Date(a.startDate || 0).getTime();
        const db = new Date(b.startDate || 0).getTime();
        return da !== db ? da - db : String(a.productName).localeCompare(String(b.productName));
      });

      const discountPayments = allPayments.filter(isDiscountPayment);
      const discountTotal = discountPayments.reduce((s, p) => s + (Number(p.paymentAmount) || 0), 0);
      const paidExcludingDiscounts = allPayments.filter((p) => !isDiscountPayment(p)).reduce((s, p) => s + (Number(p.paymentAmount) || 0), 0);
      const invoiceTotal = allLines.reduce((s, l) => s + l.grossTotal, 0);
      const balance = invoiceTotal - discountTotal - paidExcludingDiscounts;

      setPdfExcluded(new Set());
      setInvoiceHeaderNote("");
      setInvoicePreview({
        groupNames: names.join(", "),
        memberCount: totalMembers,
        invoiceLines: allLines,
        payments: allPayments,
        discountPayments,
        discountTotal,
        paidExcludingDiscounts,
        invoiceTotal,
        balance,
        gross: true,
      });
    } catch (err) {
      console.error("Failed to load combined invoice data", err);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleDownloadCombinedPdf = () => {
    if (!invoicePreview) return;
    const filteredLines = invoicePreview.invoiceLines.filter((line, idx) =>
      line.rate > 0 || !pdfExcluded.has(invoiceLineKey(line, idx))
    );
    const invoiceTotal = filteredLines.reduce((s, l) => s + l.grossTotal, 0);
    const balance = invoiceTotal - (invoicePreview.discountTotal || 0) - (invoicePreview.paidExcludingDiscounts || 0);
    generateCombinedInvoicePDF({
      ...invoicePreview,
      invoiceLines: filteredLines,
      invoiceTotal,
      balance,
      headerNote: invoiceHeaderNote,
    });
  };

  const currency = (n) => `£${Number(n || 0).toFixed(2)}`;

  // keep this in sync with header columns (excluding the leading expand cell)
  const EXPANDED_COLSPAN = 10; // ID, Group Name, Status, Campus, Start, End, Students, Adults, Notes, Actions

  const [campuses, setCampuses] = useState([]);

  useEffect(() => {
    const token = sessionStorage.getItem("bearer");
    fetch(`${SERVER_URL}api/campuses/active`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setCampuses(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to fetch campuses:", err));
  }, []);

  useEffect(() => {
    setMembersByGroup({});
    setLoadingMembers({});
    setExpanded({});
  }, [reload]);

  useEffect(() => {
    const token = sessionStorage.getItem("bearer");
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${SERVER_URL}api/campgroups/type/BOOKING`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        if (!res.ok) throw new Error(`Failed to fetch booking groups (${res.status})`);
        const data = await res.json();
        setGroups(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error loading BOOKING groups:", e);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reload]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const yearNum = Number(selectedYear);

    // Scope to the app-level year: keep groups whose stay overlaps the selected
    // year (handles a group that straddles a year boundary). Undated groups are
    // kept visible since we can't place them in a year.
    const inYear = (g) => {
      if (!Number.isFinite(yearNum)) return true;
      const sy = g.startDate ? new Date(g.startDate).getFullYear() : null;
      const ey = g.endDate ? new Date(g.endDate).getFullYear() : sy;
      if (sy == null && ey == null) return true;
      const lo = sy ?? ey;
      const hi = ey ?? sy;
      return yearNum >= lo && yearNum <= hi;
    };

    const matched = groups.filter(g => {
      if (!inYear(g)) return false;
      if (!needle) return true;
      const name = (g.groupName || "").toLowerCase();
      const notes = (g.notes || "").toLowerCase();
      const campus = (g.campus || "").toLowerCase();
      const status = (g.status || "").toLowerCase();
      return (
        name.includes(needle) ||
        notes.includes(needle) ||
        campus.includes(needle) ||
        status.includes(needle)
      );
    });

    // Default order: by start date ascending, undated groups last.
    return [...matched].sort((a, b) => {
      const ta = a.startDate ? new Date(a.startDate).getTime() : Infinity;
      const tb = b.startDate ? new Date(b.startDate).getTime() : Infinity;
      return ta - tb;
    });
  }, [groups, q, selectedYear]);

  // generic concurrency limiter for an array of IDs
  async function fetchManyWithConcurrency(ids, fn, limit = 8) {
    const results = [];
    let i = 0;
    async function worker() {
      while (i < ids.length) {
        const id = ids[i++];
        try {
          const item = await fn(id);
          results.push(item);
        } catch (e) {
          // swallow individual failures; you still get others
          console.warn("Member fetch failed for id", id, e);
        }
      }
    }
  
    const workers = Array.from({ length: Math.min(limit, ids.length) }, worker);
    await Promise.all(workers);
    return results;
  }

  const fetchAdultsByIds = async (ids, token) => {
    if (!ids?.length) return [];
    return fetchManyWithConcurrency(
      ids,
      (id) => fetchJsonAuth(`${SERVER_URL}api/adult/${id}`, token), // <-- per-ID endpoint
      6
    );
  };

  const fetchMembersForGroup = async (group) => {
    const token = sessionStorage.getItem("bearer");
    const gid = group.id;
    setLoadingMembers(prev => ({ ...prev, [gid]: true }));
    try {
      const [students, adults] = await Promise.all([
        fetchStudentsByIds(group.studentIds || [], token),
        fetchAdultsByIds(group.adultIds || [], token),
      ]);
      setMembersByGroup(prev => ({ ...prev, [gid]: { students, adults } }));
    } catch (err) {
      console.error("Failed to load members for group", group.id, err);
      setMembersByGroup(prev => ({ ...prev, [gid]: { students: [], adults: [] } }));
    } finally {
      setLoadingMembers(prev => ({ ...prev, [gid]: false }));
    }
  };

  const toggleExpand = async (group) => {
    const gid = group.id;
    const willExpand = !expanded[gid];
    setExpanded(prev => ({ ...prev, [gid]: willExpand }));
    if (willExpand && !membersByGroup[gid]) {
      await fetchMembersForGroup(group);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">Booking Groups</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={view} onChange={changeView} aria-label="View">
            <ToggleButton value="list" aria-label="List"><ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />List</ToggleButton>
            <ToggleButton value="timeline" aria-label="Timeline"><ViewTimelineIcon fontSize="small" sx={{ mr: 0.5 }} />Timeline</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="Search name / notes / campus / status"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 320 }}
          />
          {/* The group invoice endpoint needs FINANCE (403 otherwise). */}
          {showFinancials && selectedGroupIds.size > 0 && (
            <Button
              variant="contained"
              startIcon={generatingInvoice ? <CircularProgress size={16} /> : <ReceiptLongIcon />}
              onClick={handleCombinedInvoice}
              disabled={generatingInvoice}
            >
              {generatingInvoice ? "Generating…" : `Combined invoice (${selectedGroupIds.size})`}
            </Button>
          )}
          <IconButton
            title="Refresh — pick up groups imported from HubSpot since the page loaded"
            onClick={() => setReload((r) => r + 1)}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton color="primary" title="Create group" onClick={() => setAddOpen(true)}>
            <AddIcon />
          </IconButton>
        </Stack>
      </Stack>

      {view === "timeline" ? (
        <GroupsTimeline
          groups={filtered}
          selectedYear={selectedYear}
          onOpenGroup={(g) => { setSelectedGroup(g); setEditOpen(true); }}
          onSelectStudent={onSelectStudent}
        />
      ) : (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={filtered.length > 0 && selectedGroupIds.size === filtered.length}
                  indeterminate={selectedGroupIds.size > 0 && selectedGroupIds.size < filtered.length}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell />{/* expand */}
              <TableCell>ID</TableCell>
              <TableCell>Group Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Campus</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell align="right">Students</TableCell>
              <TableCell align="right">Adults</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Actions</TableCell> {/* NEW */}
            </TableRow>
          </TableHead>

          <TableBody>
            {filtered.map(g => {
              const isOpen = !!expanded[g.id];
              const studentCount = Array.isArray(g.studentIds) ? g.studentIds.length : 0;
              const adultCount = Array.isArray(g.adultIds) ? g.adultIds.length : 0;
              const members = membersByGroup[g.id];

              return (
                <React.Fragment key={g.id}>
                  <TableRow hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedGroupIds.has(g.id)}
                        onChange={() => toggleGroupSelection(g.id)}
                      />
                    </TableCell>
                    <TableCell width={48}>
                      <IconButton size="small" onClick={() => toggleExpand(g)} aria-label="expand">
                        {isOpen ? <ExpandLess/> : <ExpandMore/>}
                      </IconButton>
                    </TableCell>
                    <TableCell>{g.id}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {g.groupName}
                      {g.hubspotDealId ? (
                        <Chip
                          size="small"
                          label="HubSpot"
                          variant="outlined"
                          color="warning"
                          title={`Imported from HubSpot deal ${g.hubspotDealId}`}
                          sx={{ ml: 1, height: 20, fontSize: 11 }}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>{g.status ? <Chip size="small" label={statusLabel(g.status)} color={
                      g.status === "CONFIRMED" || g.status === "DEPOSIT_PAID" ? "success" :
                      g.status === "COMPLETE" ? "default" :
                      g.status === "CANCELLED" ? "error" :
                      g.status === "INVOICED" || g.status === "SPACE_HELD" ? "info" :
                      g.status === IMPORTED_CHECK_STATUS || g.status === "DRAFT" ? "warning" :
                      "default"
                    } variant="outlined" /> : <em>—</em>}</TableCell>
                    <TableCell>{g.campus || <em>—</em>}</TableCell>
                    <TableCell>{g.startDate ? formatLocalDateOnly(new Date(g.startDate)) : <em>—</em>}</TableCell>
                    <TableCell>{g.endDate ? formatLocalDateOnly(new Date(g.endDate)) : <em>—</em>}</TableCell>
                    <TableCell align="right">{studentCount}</TableCell>
                    <TableCell align="right">{adultCount}</TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      {g.notes ? (
                        <Typography
                          variant="body2"
                          title={g.notes}
                          sx={{
                            whiteSpace: "pre-line",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {g.notes}
                        </Typography>
                      ) : (
                        <em>—</em>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        title="Edit group"
                        onClick={() => { setSelectedGroup(g); setEditOpen(true); }}
                      >
                      <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {isOpen && (
                    <TableRow>
                      <TableCell />{/* checkbox */}
                      <TableCell />{/* expand */}
                      <TableCell colSpan={EXPANDED_COLSPAN} sx={{ bgcolor: "action.hover", p: 0 }}>
                        <Box sx={{ p: 2 }}>
                          {loadingMembers[g.id] ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <CircularProgress size={18} /> <span>Loading members…</span>
                            </Box>
                          ) : (
                            <>
                              {/* Students */}
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>Students</Typography>
                              <Table size="small" aria-label="student members">
                                <TableHead>
                                  <TableRow>
                                    <TableCell width={80}>ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Nationality</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(members?.students || []).length ? (
                                    members.students.map(s => {
                                      const name = [s.studentName, s.studentSurname]
                                        .filter(Boolean)
                                        .join(" ") || `Student ${s.id}`;
                                      return (
                                        <TableRow key={`s-${s.id}`}>
                                          <TableCell>{s.id}</TableCell>
                                          <TableCell>{name}</TableCell>
                                          <TableCell>{s.studentNationality || <em>—</em>}</TableCell>
                                        </TableRow>
                                      );
                                    })
                                  ) : (
                                    (Array.isArray(g.studentIds) && g.studentIds.length > 0) ? (
                                      g.studentIds.map(id => (
                                        <TableRow key={`sid-${id}`}>
                                          <TableCell>{id}</TableCell>
                                          <TableCell colSpan={2}><em>Details unavailable</em></TableCell>
                                        </TableRow>
                                      ))
                                    ) : (
                                      <TableRow>
                                        <TableCell colSpan={3}><em>No students</em></TableCell>
                                      </TableRow>
                                    )
                                  )}
                                </TableBody>
                              </Table>

                              {/* Adults */}
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Adults</Typography>
                              <Table size="small" aria-label="adult members">
                                <TableHead>
                                  <TableRow>
                                    <TableCell width={80}>ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Notes</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(members?.adults || []).length ? (
                                    members.adults.map(a => {
                                      const name = [
                                        a.adultName,
                                        a.adultSurname,
                                        a.first_name,
                                        a.last_name,
                                        a.name,
                                      ]
                                        .filter(Boolean)
                                        .join(" ")
                                        .trim() || `Adult ${a.id}`;
                                      return (
                                        <TableRow key={`a-${a.id}`}>
                                          <TableCell>{a.id}</TableCell>
                                          <TableCell>{name}</TableCell>
                                          <TableCell>{a.notes || <em>—</em>}</TableCell>
                                        </TableRow>
                                      );
                                    })
                                  ) : (
                                    (Array.isArray(g.adultIds) && g.adultIds.length > 0) ? (
                                      g.adultIds.map(id => (
                                        <TableRow key={`aid-${id}`}>
                                          <TableCell>{id}</TableCell>
                                          <TableCell colSpan={2}><em>Details unavailable</em></TableCell>
                                        </TableRow>
                                      ))
                                    ) : (
                                      <TableRow>
                                        <TableCell colSpan={3}><em>No adults</em></TableCell>
                                      </TableRow>
                                    )
                                  )}
                                </TableBody>
                              </Table>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Add dialog */}
      <AddBookingGroup
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => setReload(r => r + 1)}
        campuses={campuses}
        defaultCampus=""       // optional
      />
      {selectedGroup && (
        <EditBookingGroup
          open={editOpen}
          onClose={() => { setEditOpen(false); setSelectedGroup(null); }}
          group={selectedGroup}
          campuses={campuses}
          showFinancials={showFinancials}
          onUpdated={() => setReload(r => r + 1)}
          onMembersChanged={(groupId, nextStudentIds, nextAdultIds) => {
            // ✅ update the table rows immediately
            setGroups(prev =>
              prev.map(g =>
              g.id === groupId ? { ...g, studentIds: nextStudentIds, adultIds: nextAdultIds } : g
            )
            );

            // ✅ keep the open dialog’s group prop in sync too (important)
            setSelectedGroup(prev =>
              prev && prev.id === groupId
                ? { ...prev, studentIds: nextStudentIds, adultIds: nextAdultIds }
                : prev
            );

            // ✅ expanded cache should be invalid now
            setMembersByGroup(prev => {
              const copy = { ...prev };
              delete copy[groupId];
              return copy;
            });
          }}
        />
      )}

      {/* Combined invoice preview dialog */}
      <Dialog
        open={!!invoicePreview}
        onClose={() => setInvoicePreview(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Combined invoice preview</DialogTitle>
        <DialogContent>
          {invoicePreview && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {invoicePreview.groupNames} — {invoicePreview.memberCount} members
              </Typography>
              <TextField
                label="Invoice header note (optional)"
                placeholder="School / agent name and address, VAT number, etc."
                helperText="Free text shown in the invoice header"
                value={invoiceHeaderNote}
                onChange={(e) => setInvoiceHeaderNote(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              />
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>End</TableCell>
                      <TableCell align="right">Unit</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoicePreview.invoiceLines.map((line, idx) => {
                      const key = invoiceLineKey(line, idx);
                      const excluded = pdfExcluded.has(key);
                      return (
                        <TableRow key={key} sx={excluded ? { opacity: 0.4 } : {}}>
                          <TableCell padding="checkbox">
                            {line.rate === 0 ? (
                              <Checkbox
                                size="small"
                                checked={!excluded}
                                onChange={() => togglePdfExclude(key)}
                              />
                            ) : null}
                          </TableCell>
                          <TableCell align="right">{line.qty}</TableCell>
                          <TableCell>{line.productName}</TableCell>
                          <TableCell>
                            {line.startDate ? formatLocalDateOnly(new Date(line.startDate)) : ""}
                          </TableCell>
                          <TableCell>
                            {line.endDate ? formatLocalDateOnly(new Date(line.endDate)) : ""}
                          </TableCell>
                          <TableCell align="right">{currency(line.rate)}</TableCell>
                          <TableCell align="right">{currency(line.grossTotal)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">
                  Total: <strong>{currency(invoicePreview.invoiceTotal)}</strong>
                  {invoicePreview.paidExcludingDiscounts > 0 && (
                    <> &nbsp;|&nbsp; Paid: {currency(invoicePreview.paidExcludingDiscounts)}</>
                  )}
                  &nbsp;|&nbsp; Balance: <strong>{currency(invoicePreview.balance)}</strong>
                </Typography>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoicePreview(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownloadCombinedPdf}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
